import { useCallback, useMemo, useState } from 'react';
import { MdDelete, MdDownload } from 'react-icons/md';

import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { DigitalNavigationLog } from '@/store/digital-navigation-log/digital-navigation-log.dto';
import {
  useDeleteDigitalNavigationLogMutation,
  useDigitalNavigationLogPdfMutation,
  useDigitalNavigationLogsQuery,
} from '@/store/digital-navigation-log/digital-navigation-log.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { getBrowserTimeZone } from '@/utils/timezone';
import ActionsButton from '@components/ActionsButton/ActionsButton.tsx';
import DataTable, { type Column } from '@components/DataTable/DataTable.tsx';

import s from './NavigationLogHistory.module.scss';

const PAGE_LIMIT = 100;

const formatNameDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

type Props = {
  search?: string;
  onSelectLog?: (iso: string) => void;
};

export default function NavigationLogHistory({ search = '', onSelectLog }: Props) {
  const { data, fetchNextPage, hasNextPage, isFetching } =
    useDigitalNavigationLogsQuery(PAGE_LIMIT);
  const deleteMutation = useDeleteDigitalNavigationLogMutation();
  const pdfMutation = useDigitalNavigationLogPdfMutation();
  const { can } = usePermissions();
  const canDelete = can(PERMISSIONS.logbook.DigitalNavigationLogDelete);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const rows = useMemo<DigitalNavigationLog[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const formatted = formatNameDate(row.date);
      return (
        formatted.toLowerCase().includes(q) ||
        row.date.toLowerCase().includes(q) ||
        row.passageFrom.toLowerCase().includes(q) ||
        row.passageTo.toLowerCase().includes(q) ||
        `navigation log ${formatted}`.toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const handleDownload = useCallback(
    async (row: DigitalNavigationLog) => {
      setDownloadingId(row.id);
      try {
        const blob = await pdfMutation.mutateAsync({
          id: row.id,
          timezone: getBrowserTimeZone(),
        });
        await downloadBlob(blob, `navigation-log-${row.date || row.id}.pdf`);
      } finally {
        setDownloadingId(null);
      }
    },
    [pdfMutation],
  );

  const handleDelete = useCallback(
    async (row: DigitalNavigationLog) => {
      await deleteMutation.mutateAsync({ id: row.id });
    },
    [deleteMutation],
  );

  const columns: Column<DigitalNavigationLog>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        sortValue: (row) => row.date,
        render: (_v, row) => `Navigation Log ${formatNameDate(row.date)}`,
      },
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        sortValue: (row) => row.date,
        render: (_v, row) => formatNameDate(row.date),
      },
      {
        key: 'passageFrom',
        label: 'From',
        sortable: true,
        sortValue: (row) => row.passageFrom,
        render: (_v, row) => row.passageFrom || '-',
      },
      {
        key: 'passageTo',
        label: 'To',
        sortable: true,
        sortValue: (row) => row.passageTo,
        render: (_v, row) => row.passageTo || '-',
      },
      {
        key: 'actions',
        label: 'Actions',
        width: '120px',
        render: (_v, row) => (
          <ActionsButton
            ariaLabel="Navigation log actions"
            items={[
              {
                label: downloadingId === row.id ? 'Downloading…' : 'Download PDF',
                icon: <MdDownload />,
                onClick: () => void handleDownload(row),
              },
              ...(canDelete
                ? [
                    {
                      label: 'Delete',
                      icon: <MdDelete />,
                      variant: 'danger' as const,
                      onClick: () => void handleDelete(row),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [canDelete, downloadingId, handleDelete, handleDownload],
  );

  return (
    <>
      <DataTable
        autoFit={false}
        data={filtered}
        columns={columns}
        itemsPerPage={10}
        itemsPerBigPage={10}
        onRowClick={onSelectLog ? (row) => onSelectLog(row.date) : undefined}
      />
      {hasNextPage && !isFetching && (
        <button type="button" onClick={() => void fetchNextPage()} className={s.loadMore}>
          Load More
        </button>
      )}
    </>
  );
}
